import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  DataTable,
  InlineStack,
  Layout,
  Modal,
  Select,
  Spinner,
  Text,
  TextField,
} from '@shopify/polaris';
import { EditIcon } from '@shopify/polaris-icons';
import { productsAPI } from '../services/api';
import { resizeConditionImage } from '../utils/conditionImage';

const CONDITION_LABELS = {
  new: 'New',
  very_good: 'Very good',
  good: 'Good',
  fair: 'Fair',
};

const CONDITION_TONES = {
  new: 'success',
  very_good: 'info',
  good: 'attention',
  fair: 'warning',
};

const conditionLabel = (condition) => CONDITION_LABELS[condition] || condition || 'Unknown';

const formatDate = (value) => {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

const ConditionsDashboard = () => {
  const [data, setData] = useState({ summary: {}, products: [] });
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [editedStatus, setEditedStatus] = useState('available');
  const [editedCondition, setEditedCondition] = useState('new');
  const [editedComment, setEditedComment] = useState('');
  const [editedImage, setEditedImage] = useState(null);
  const [imageProcessing, setImageProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updateError, setUpdateError] = useState('');
  const [barcodeItem, setBarcodeItem] = useState(null);
  const [editedBarcode, setEditedBarcode] = useState('');
  const [savingBarcode, setSavingBarcode] = useState(false);
  const [barcodeError, setBarcodeError] = useState('');
  const imageInputRef = useRef(null);

  const fetchConditions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await productsAPI.getConditionDashboard();
      setData(response.data || { summary: {}, products: [] });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load product conditions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConditions();
  }, [fetchConditions]);

  const openEditor = (product, item) => {
    setEditingItem({ product, item });
    setEditedCondition(item.condition);
    setEditedStatus(item.status || 'available');
    setEditedComment(item.comment || '');
    setEditedImage(item.image || null);
    setUpdateError('');
  };

  const closeEditor = () => {
    if (saving) return;
    setEditingItem(null);
    setUpdateError('');
  };

  const saveItem = async () => {
    if (!editingItem) return;
    try {
      setSaving(true);
      setUpdateError('');
      await productsAPI.updateCondition(
        editingItem.product.id,
        editingItem.item.id,
        { condition: editedCondition, status: editedStatus, comment: editedComment, image: editedImage }
      );
      setEditingItem(null);
      await fetchConditions();
    } catch (err) {
      setUpdateError(err.response?.data?.error || 'Failed to update item condition');
    } finally {
      setSaving(false);
    }
  };

  const selectImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      setImageProcessing(true);
      setUpdateError('');
      setEditedImage(await resizeConditionImage(file));
    } catch (err) {
      setUpdateError(err.message || 'Failed to process image');
    } finally {
      setImageProcessing(false);
    }
  };

  const openBarcodeEditor = (product, item) => {
    setBarcodeItem({ product, item });
    setEditedBarcode(item.barcode || '');
    setBarcodeError('');
  };

  const closeBarcodeEditor = () => {
    if (savingBarcode) return;
    setBarcodeItem(null);
    setBarcodeError('');
  };

  const saveBarcode = async () => {
    if (!barcodeItem) return;
    const barcode = editedBarcode.trim();
    if (!/^[A-Za-z0-9]{1,255}$/.test(barcode)) {
      setBarcodeError('Barcode must contain 1 to 255 letters and numbers only');
      return;
    }
    try {
      setSavingBarcode(true);
      setBarcodeError('');
      await productsAPI.updateItemBarcode(barcodeItem.product.id, barcodeItem.item.id, barcode);
      setBarcodeItem(null);
      await fetchConditions();
    } catch (err) {
      setBarcodeError(err.response?.data?.error || 'Failed to update barcode');
    } finally {
      setSavingBarcode(false);
    }
  };

  const filteredProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return data.products || [];
    return (data.products || []).filter((product) => {
      const itemText = (product.conditions || []).map((item) => [
        item.id,
        item.barcode,
        item.condition,
        item.status || 'available',
        conditionLabel(item.condition),
        item.comment,
      ].filter(Boolean).join(' ')).join(' ');
      return `${product.title || ''} ${product.id} ${itemText}`.toLowerCase().includes(normalizedQuery);
    });
  }, [data.products, query]);

  const rows = filteredProducts.flatMap((product) => {
    if (!product.conditions?.length) {
      return [[
        <BlockStack key={`product-${product.id}`} gap="050">
          <Text variant="bodyMd" fontWeight="semibold">{product.title}</Text>
          <Text variant="bodySm" tone="subdued">Product #{product.id}</Text>
        </BlockStack>,
        '—',
        '—',
        <Badge key={`untracked-${product.id}`} tone="attention">Not tracked</Badge>,
        '—',
        '—',
        '—',
        '—',
        '—',
      ]];
    }

    return product.conditions.map((item) => [
      <BlockStack key={`product-${product.id}-${item.id}`} gap="050">
        <Text variant="bodyMd" fontWeight="semibold">{product.title}</Text>
        <Text variant="bodySm" tone="subdued">
          {product.trackedQuantity} of {product.totalQuantity} items tracked
        </Text>
      </BlockStack>,
      <BlockStack key={`item-${item.id}`} gap="050">
        <InlineStack gap="100" blockAlign="center" wrap={false}>
          <Text variant="bodyMd">{item.barcode || 'No barcode'}</Text>
          <Button
            variant="plain"
            icon={EditIcon}
            accessibilityLabel={`Edit barcode for item ${item.id}`}
            onClick={() => openBarcodeEditor(product, item)}
          />
        </InlineStack>
        <Text variant="bodySm" tone="subdued">Item #{item.id}</Text>
      </BlockStack>,
      item.image ? (
        <img
          key={`image-${item.id}`}
          src={item.image}
          alt={`Condition of ${product.title}, item ${item.id}`}
          width={72}
          height={72}
          style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6 }}
        />
      ) : (
        <Text key={`image-empty-${item.id}`} tone="subdued">No image</Text>
      ),
      <Badge key={`condition-${item.id}`} tone={CONDITION_TONES[item.condition]}>
        {conditionLabel(item.condition)}
      </Badge>,
      <Badge key={`status-${item.id}`} tone={item.status === 'unavailable' ? 'critical' : 'success'}>
        {item.status === 'unavailable' ? 'Unavailable' : 'Available'}
      </Badge>,
      item.comment || '—',
      formatDate(item.updatedAt),
      item.history?.length ? (
        <div key={`history-${item.id}`} style={{ maxHeight: 140, minWidth: 230, overflowY: 'auto' }}>
          <BlockStack gap="100">
            {item.history.map((entry) => (
              <Text key={entry.id} variant="bodySm">
                {formatDate(entry.changedAt)}: {conditionLabel(entry.previousCondition)} → {conditionLabel(entry.condition)}
              </Text>
            ))}
          </BlockStack>
        </div>
      ) : (
        <Text key={`history-empty-${item.id}`} variant="bodySm" tone="subdued">No changes yet</Text>
      ),
      <Button key={`edit-${item.id}`} size="slim" onClick={() => openEditor(product, item)}>
        Edit
      </Button>,
    ]);
  });

  if (loading) {
    return (
      <Layout>
        <Layout.Section>
          <Card>
            <InlineStack align="center" gap="300">
              <Spinner size="large" />
              <Text>Loading product conditions…</Text>
            </InlineStack>
          </Card>
        </Layout.Section>
      </Layout>
    );
  }

  const summaryCards = [
    ['Products', data.summary?.products || 0],
    ['Physical items', data.summary?.totalQuantity || 0],
    ['Tracked', data.summary?.trackedQuantity || 0],
    ['Untracked', data.summary?.untrackedQuantity || 0],
    ['Unavailable', data.summary?.unavailableQuantity || 0],
  ];

  return (
    <BlockStack gap="400">
      <InlineStack align="space-between">
        <BlockStack gap="100">
          <Text as="h1" variant="headingLg">Product Conditions</Text>
          <Text as="p" tone="subdued">Current condition and complete change history for every physical item.</Text>
        </BlockStack>
        <Button onClick={fetchConditions}>Refresh</Button>
      </InlineStack>

      {error && (
        <Banner tone="critical" onDismiss={() => setError('')}>
          {error}
        </Banner>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 16 }}>
        {summaryCards.map(([label, value]) => (
          <Card key={label}>
            <BlockStack gap="100">
              <Text tone="subdued">{label}</Text>
              <Text variant="headingLg">{value}</Text>
            </BlockStack>
          </Card>
        ))}
      </div>

      <Card>
        <BlockStack gap="300">
          <TextField
            label="Search conditions"
            labelHidden
            value={query}
            onChange={setQuery}
            placeholder="Search by product, item ID, barcode, condition, status, or comment"
            clearButton
            onClearButtonClick={() => setQuery('')}
          />
          {rows.length > 0 ? (
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
              headings={['Product', 'Physical item', 'Image', 'Condition', 'Availability', 'Comment', 'Last updated', 'History', 'Action']}
              rows={rows}
              footerContent={`${rows.length} physical item row${rows.length === 1 ? '' : 's'} shown`}
            />
          ) : (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <Text tone="subdued">No products or items match this search.</Text>
            </div>
          )}
        </BlockStack>
      </Card>

      <Modal
        open={Boolean(editingItem)}
        onClose={closeEditor}
        title={editingItem
          ? `Update ${editingItem.product.title} — ${editingItem.item.barcode || `Item #${editingItem.item.id}`}`
          : 'Update item condition'}
        primaryAction={{
          content: 'Save condition',
          onAction: saveItem,
          loading: saving,
          disabled: saving || imageProcessing,
        }}
        secondaryActions={[{
          content: 'Cancel',
          onAction: closeEditor,
          disabled: saving,
        }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {updateError && <Banner tone="critical">{updateError}</Banner>}
            <Select
              label="Availability status"
              options={[
                { label: 'Available', value: 'available' },
                { label: 'Unavailable', value: 'unavailable' },
              ]}
              value={editedStatus}
              onChange={setEditedStatus}
              disabled={saving}
              helpText="Unavailable items remain in inventory but cannot be rented or packed. Add the reason in the comment."
            />
            <Select
              label="Condition"
              options={[
                { label: 'New', value: 'new' },
                { label: 'Very good', value: 'very_good' },
                { label: 'Good', value: 'good' },
                { label: 'Fair', value: 'fair' },
              ]}
              value={editedCondition}
              onChange={setEditedCondition}
              disabled={saving}
            />
            <TextField
              label="Comment"
              value={editedComment}
              onChange={setEditedComment}
              multiline={4}
              maxLength={2000}
              showCharacterCount
              autoComplete="off"
              disabled={saving}
            />
            <BlockStack gap="200">
              <Text fontWeight="semibold">Condition image</Text>
              {editedImage ? (
                <img
                  src={editedImage}
                  alt="Condition preview"
                  style={{
                    width: '100%',
                    maxWidth: 360,
                    maxHeight: 260,
                    objectFit: 'contain',
                    borderRadius: 8,
                    border: '1px solid #dfe3e8',
                  }}
                />
              ) : (
                <Text tone="subdued">No condition image added.</Text>
              )}
              <InlineStack gap="200">
                <Button
                  onClick={() => imageInputRef.current?.click()}
                  loading={imageProcessing}
                  disabled={saving || imageProcessing}
                >
                  {editedImage ? 'Replace image' : 'Add image'}
                </Button>
                {editedImage && (
                  <Button
                    tone="critical"
                    variant="plain"
                    onClick={() => setEditedImage(null)}
                    disabled={saving || imageProcessing}
                  >
                    Remove image
                  </Button>
                )}
              </InlineStack>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={selectImage}
                style={{ display: 'none' }}
              />
              <Text tone="subdued" variant="bodySm">
                Images are converted to JPEG, resized to a maximum of 1600 px on the longest side,
                and compressed to no more than 1 MB.
              </Text>
            </BlockStack>
            <Text tone="subdued" variant="bodySm">
              A history entry will be added automatically when the condition changes.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Modal
        open={Boolean(barcodeItem)}
        onClose={closeBarcodeEditor}
        title={barcodeItem ? `Update barcode — Item #${barcodeItem.item.id}` : 'Update barcode'}
        primaryAction={{
          content: 'Save barcode',
          onAction: saveBarcode,
          loading: savingBarcode,
          disabled: savingBarcode || !editedBarcode.trim()
            || editedBarcode.trim() === (barcodeItem?.item.barcode || ''),
        }}
        secondaryActions={[{
          content: 'Cancel',
          onAction: closeBarcodeEditor,
          disabled: savingBarcode,
        }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            {barcodeError && <Banner tone="critical">{barcodeError}</Banner>}
            <TextField
              label="Barcode"
              value={editedBarcode}
              onChange={setEditedBarcode}
              autoComplete="off"
              maxLength={255}
              helpText="Use letters and numbers only. A barcode assigned to another item cannot be reused."
              disabled={savingBarcode}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </BlockStack>
  );
};

export default ConditionsDashboard;
